import { createFileRoute } from "@tanstack/react-router";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { HOME_FAQS, HOME_SERVICE_NODES } from "@/data/home-core";
import HomePage from "@/components/home/HomePage";
import heroEarth640Avif from "@/assets/hero-earth-640.avif";
import heroEarth1280Avif from "@/assets/hero-earth-1280.avif";
import heroEarth1920Avif from "@/assets/hero-earth-1920.avif";

const SITE_URL = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/")({
  component: HomePage,
  head: () => ({
    meta: [
      // Per-page overrides — root sets defaults; we sharpen for CTR.
      // D3 CTR polish: brand-first title so "nyc language institute" brand query (pos 3.1, CTR 0%) matches user intent instantly.
      { title: ctaTitle("NYC Language Institute — Notary · MFA · NAATI Bangkok", "/index", "th")},
      { name: "description", content: ctaDesc("Notary Public + แปลรับรอง 50+ ภาษา รับรอง MFA / สถานทูต / NAATI Australia · ทั่วไทย 77 จังหวัด · ตั้งแต่ปี 2010 · LINE @NYCLI — เริ่มต้นง่าย ๆ แค่ทักไลน์ เราดูแลจนจบทุกขั้นตอน", "/index", "th")},
      { property: "og:title", content: ctaTitle("NYC Language Institute — Notary Bangkok · แปลรับรอง MFA · NAATI", "/index", "th")},
      { property: "og:description", content: ctaDesc("NYC Language Institute · Notary Public ทนายรับรองศาล + แปลรับรอง 50+ ภาษา รับรอง MFA/สถานทูต/NAATI ครอบคลุม 77 จังหวัด · ตอบใน 15 นาที · ตั้งแต่ปี 2010", "/index", "th")},
      { property: "og:url", content: SITE_URL + "/" },
      { property: "og:site_name", content: "NYC Language Institute" },
      { property: "og:locale", content: "th_TH" },
      { property: "og:locale:alternate", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: SITE_URL + "/" },
      { rel: "alternate", hrefLang: "th", href: SITE_URL + "/" },
      { rel: "alternate", hrefLang: "en", href: SITE_URL + "/" },
      { rel: "alternate", hrefLang: "x-default", href: SITE_URL + "/" },
      // LCP: preload hero background image (responsive AVIF; browsers that don't support AVIF ignore the preload and fall back to CSS image-set)
      {
        rel: "preload",
        as: "image",
        href: heroEarth1280Avif,
        type: "image/avif",
        fetchPriority: "high",
        imageSrcSet: `${heroEarth640Avif} 640w, ${heroEarth1280Avif} 1280w, ${heroEarth1920Avif} 1920w`,
        imageSizes: "100vw",
      } as unknown as { rel: string; href: string },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            // NOTE: Organization / WebSite / LocalBusiness are emitted ONCE by
            // src/routes/__root.tsx as the site-wide primary @graph. This route
            // only adds page-specific nodes and references them via @id to
            // avoid duplicate entities (which triggered Schema.org validator
            // errors + 50+ warnings previously).
            {
              "@type": "WebPage",
              "@id": SITE_URL + "/#webpage",
              url: SITE_URL + "/",
              name: "NYC Language Institute — แปลรับรอง · Notary · MFA",
              isPartOf: { "@id": SITE_URL + "/#website" },
              about: { "@id": SITE_URL + "/#organization" },
              primaryImageOfPage: { "@type": "ImageObject", url: "https://nyclanguageinstitute.com/social/nyc-legal-og.webp" },
              inLanguage: "th-TH",
            },
            {
              "@type": "ItemList",
              "@id": SITE_URL + "/#services",
              name: "NYC Core Services",
              itemListElement: HOME_SERVICE_NODES.map((d, i) => ({
                "@type": "Service",
                position: i + 1,
                name: d.title,
                description: d.desc,
                provider: { "@id": SITE_URL + "/#organization" },
                areaServed: "Thailand",
                url: d.siteUrl,
              })),
            },
            {
              "@type": "FAQPage",
              "@id": SITE_URL + "/#faq",
              mainEntity: HOME_FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            // VideoObject removed — no real video asset yet. Re-add when a
            // real contentUrl/embedUrl is available (Google penalizes fake
            // video markup).

            {
              "@type": "ItemList",
              "@id": SITE_URL + "/#courses",
              name: "NYC Document Preparation Mini-Courses",
              itemListElement: [
                {
                  "@type": "Course",
                  position: 1,
                  name: "วิธีเตรียมเอกสารส่งรับรองกงสุล (MFA Legalization 101)",
                  description: "คู่มือฟรี: เอกสารที่ต้องเตรียม ขั้นตอน ค่าธรรมเนียม และระยะเวลา 2-7 วันทำการ",
                  url: SITE_URL + "/guides",
                  provider: { "@id": SITE_URL + "/#organization" },
                  inLanguage: "th",
                  hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: "PT30M", inLanguage: "th" },
                  offers: { "@type": "Offer", category: "Free", price: "0", priceCurrency: "THB", availability: "https://schema.org/InStock", url: SITE_URL + "/guides" },
                },
                {
                  "@type": "Course",
                  position: 2,
                  name: "Notary Public ในไทย — ใช้เมื่อไหร่ และต่างจากกงสุลอย่างไร",
                  description: "เรียนรู้ความแตกต่างระหว่าง Notarial Services Attorney และ MFA Legalization ในประเทศไทย",
                  url: SITE_URL + "/services/notary-public",
                  provider: { "@id": SITE_URL + "/#organization" },
                  inLanguage: "th",
                  hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: "PT20M", inLanguage: "th" },
                  offers: { "@type": "Offer", category: "Free", price: "0", priceCurrency: "THB", availability: "https://schema.org/InStock", url: SITE_URL + "/services/notary-public" },
                },
                {
                  "@type": "Course",
                  position: 3,
                  name: "Apostille vs Legalization — เลือกแบบไหนสำหรับประเทศปลายทาง",
                  description: "ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญา Apostille เมื่อ 30 มิ.ย. 2569 (มีผลบังคับใช้กับไทย 28 ก.พ. 2570)",
                  url: SITE_URL + "/apostille",
                  provider: { "@id": SITE_URL + "/#organization" },
                  inLanguage: "th",
                  hasCourseInstance: { "@type": "CourseInstance", courseMode: "online", courseWorkload: "PT25M", inLanguage: "th" },
                  offers: { "@type": "Offer", category: "Free", price: "0", priceCurrency: "THB", availability: "https://schema.org/InStock", url: SITE_URL + "/apostille" },
                },
              ],
            },
            // VideoObject spread removed — VIDEOS entries currently point
            // to placeholder YouTube URLs; re-enable once real videos ship.

          ],
        }),
      },
    ],
  }),
});
